import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@cuito/ui';
import type { OfflineQueueStore } from '@cuito/api-client';
import type { OrderStatus } from '@cuito/types';
import { readErrorCode, readServerMessage } from '../../lib/apiError';
import { useCashierStream } from '../CashierLayout';
import { acceptOrder, type OrderQueueItem } from '../api/orders';
import NewOrderToast, {
  newOrderSoundReady,
  playNewOrderChime,
  unlockNewOrderSound,
} from '../components/NewOrderToast';
import OrderCard from '../components/OrderCard';
import { formatDataAsOf, isStale } from '../data/dataAsOf';
import { BOARD_GEOM_GAP, BOARD_GEOM_WIDTH, geomAttrs } from '../lib/boardGeometry';
import { readSoundEnabled } from '../lib/prefs';
import { offlineActions } from '../offline/offlineActions';
import { browserOfflineQueue, enqueueOperation } from '../offline/queue';
import {
  BOARD_SECTIONS,
  SECTION_LABELS,
  groupBySection,
  type BoardSection,
} from '../realtime/orderQueue';

/** 离线接单的如实提示（§6.4 规则 5 的句式：`当前离线，…将在联网后上传`） */
export const OFFLINE_ACCEPT_QUEUED_TEXT = '当前离线，接单记录将在联网后上传';
/** 本机没有离线队列库时的如实提示：**不许**假装接单成功 */
export const BOARD_OFFLINE_QUEUE_UNAVAILABLE_TEXT = '本机没有离线队列，离线接单无法记录，请联网后再接单';

export interface CashierBoardPageProps {
  /** 测试注入用（`undefined` = 浏览器里那个 IndexedDB 队列；`null` = 本机没有队列） */
  offlineStore?: OfflineQueueStore | null;
}

/**
 * 接单面板（§3.3 `/cashier`）：四分区 + 实时插卡 + 提示音 + 三个取单入口。
 *
 * 三件事在这里收口，别处不要再写：
 *   ① **本地乐观状态**（`localStatus`）：接单成功或 409 之后立刻把卡片移出待接单。
 *      没有它的话界面要等服务端广播（socket 断开时永远不来）；
 *      服务端状态一到（`status !== PENDING`）就让位给真值。
 *   ② **提示音的生命周期**：解锁（首次交互）→ 到达播放 → 每 10 秒重复最多 3 次。
 *   ③ **`document.title` 前缀**（§6.2：`(3) CUITO 收银端`）。
 *
 * **判据与金额一律只读服务端**：分区用 `groupBySection`（按 `status`）、件数用队列项的
 * `itemCount`、金额直接取 `totalAmount`；本页没有任何业务重算（§4.5）。
 *
 * ⚠️ 根锚点 `cashier-board-page` 是 Task 13 冻结的（`routes.spec.tsx` 逐条断言「路径 →
 * 根锚点」），换成真面板后必须保留 —— 新锚点只能追加。
 */
export const REMINDER_INTERVAL_MS = 10_000;
export const MAX_REMINDER_COUNT = 3;
/** 新插入卡片的 2 秒高亮（§6.2） */
export const HIGHLIGHT_MS = 2_000;
/** 「提示音已就绪」提示的停留时间 */
export const NOTICE_MS = 3_000;

const EMPTY_TEXT: Record<BoardSection, string> = {
  PENDING: '暂无待接订单',
  ACCEPTED: '暂无已接订单',
  PICKING: '暂无挑拣中的订单',
  READY: '暂无可交付的订单',
};

const PAGE_TITLE = 'CUITO 收银端';
const TENANT_TITLE = 'CUITO 社团管理平台';

export default function CashierBoardPage({ offlineStore }: CashierBoardPageProps = {}) {
  /**
   * 队列**全部**来自 `CashierLayout` 持有的这一份流；本页不自己拉 `GET /orders`。
   *
   * 「冷启动就能看到既有待接单」（Task 24 验收缺陷 1）由 `useOrderStream` 在 `joined`
   * 回执之后主动补拉解决：首连的 `join` 不带 `lastEventAt`，服务端只回 `pendingOrderCount`
   * 而**不发** `changedOrders`。在这里再拉一次会与那条通道重复（同一个端点、同一份合并逻辑）。
   */
  const stream = useCashierStream();
  const navigate = useNavigate();
  /** 离线队列库（Phase 9）：断网接单进它；`useMemo` 的必要性见 `CashierOrderPage` 的同名注释 */
  const queueStore = useMemo<OfflineQueueStore | null>(
    () => (offlineStore === undefined ? browserOfflineQueue() : offlineStore),
    [offlineStore],
  );

  const [section, setSection] = useState<BoardSection>('PENDING');
  const [localStatus, setLocalStatus] = useState<Record<string, OrderStatus>>({});
  const [unread, setUnread] = useState<Record<string, boolean>>({});
  const [highlighted, setHighlighted] = useState<Record<string, boolean>>({});
  const [toastOrderId, setToastOrderId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [soundEnabled] = useState(() => readSoundEnabled());
  const [soundReady, setSoundReady] = useState(() => newOrderSoundReady());
  const seenRef = useRef<Set<string>>(new Set());

  /** 队列 = 服务端流 + 本地乐观状态（仅覆盖仍是 PENDING 的那些单） */
  const orders = useMemo(
    () =>
      stream.orders.map((order) =>
        localStatus[order.id] === undefined ? order : { ...order, status: localStatus[order.id]! },
      ),
    [stream.orders, localStatus],
  );

  /**
   * 服务端状态一旦离开 PENDING，本地乐观值就让位（否则会永久盖住真值）。
   *
   * 未读点用**同一条判据**收掉：§6.2 说它是「接单后消失」，而「接单」的三种到达方式
   * （自己接成功、409 抢单输家、别人的 `order:accepted` 广播）在数据上都表现为
   * 「服务端说它不是 PENDING 了」⇒ 判据只留这一处，别在三个 handler 里各写一遍。
   */
  useEffect(() => {
    const leftPending = (id: string): boolean => {
      const order = stream.orders.find((entry) => entry.id === id);
      return order !== undefined && order.status !== 'PENDING';
    };
    const dropWhere = <T,>(current: Record<string, T>): Record<string, T> => {
      const stale = Object.keys(current).filter(leftPending);
      if (stale.length === 0) return current;
      const next = { ...current };
      for (const id of stale) delete next[id];
      return next;
    };
    setLocalStatus(dropWhere);
    setUnread(dropWhere);
  }, [stream.orders]);

  // 新单到达：未读点 + 2 秒高亮 + 浮层 + 提示音 + 触感
  useEffect(() => {
    const fresh = stream.orders.filter(
      (order) => order.status === 'PENDING' && !seenRef.current.has(order.id),
    );
    if (fresh.length === 0) return;
    for (const order of fresh) seenRef.current.add(order.id);

    setUnread((current) => {
      const next = { ...current };
      for (const order of fresh) next[order.id] = true;
      return next;
    });
    setHighlighted((current) => {
      const next = { ...current };
      for (const order of fresh) next[order.id] = true;
      return next;
    });
    setToastOrderId(fresh[0]!.id);
    if (soundEnabled) void playNewOrderChime();
    navigator.vibrate?.([80, 40, 80]);

    const timer = setTimeout(() => {
      setHighlighted((current) => {
        const next = { ...current };
        for (const order of fresh) delete next[order.id];
        return next;
      });
    }, HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [stream.orders, soundEnabled]);

  // 解锁提示音：只挂一次，两个事件都试（触摸屏没有键盘、桌面调试时没有 pointer）
  useEffect(() => {
    if (soundReady) return;
    const unlock = (): void => {
      unlockNewOrderSound();
      setSoundReady(true);
      setNotice('提示音已就绪');
    };
    window.addEventListener('keydown', unlock);
    window.addEventListener('pointerdown', unlock);
    return () => {
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('pointerdown', unlock);
    };
  }, [soundReady]);

  useEffect(() => {
    if (notice === null) return;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const sections = useMemo(() => groupBySection(orders), [orders]);
  const pendingCount = sections.PENDING.length;

  // §6.2 的文档标题前缀：待接单数量 > 0 时带上，全部接完还原
  useEffect(() => {
    document.title = pendingCount > 0 ? `(${pendingCount}) ${PAGE_TITLE}` : PAGE_TITLE;
    return () => {
      document.title = TENANT_TITLE;
    };
  }, [pendingCount]);

  // §6.2 的重复提醒：每 10 秒一次，最多 3 次（第 4 次之后只保留视觉提醒）
  useEffect(() => {
    if (pendingCount === 0 || !soundEnabled || !soundReady) return;
    let played = 0;
    const timer = setInterval(() => {
      if (played >= MAX_REMINDER_COUNT) {
        clearInterval(timer);
        return;
      }
      played += 1;
      void playNewOrderChime();
    }, REMINDER_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pendingCount, soundEnabled, soundReady]);

  const accept = useCallback(
    async (order: OrderQueueItem): Promise<void> => {
      setAcceptingId(order.id);
      setError(null);
      /*
       * 离线接单（§6.8「离线可用范围」：接单面板全部可写，进离线队列）。
       *
       * 乐观更新的形状与在线那条路**完全一致**（同一段 `setLocalStatus` / `setUnread` /
       * `setToastOrderId`），差别只在「效果从哪来」：在线是服务端确认，离线是**本机队列记下了**。
       * 顺序不能反 —— 先入队、成功之后才动界面：队列写不进去（本机没有 IndexedDB）时
       * 界面必须停在原地并如实报错，否则店员会以为这单接下了。
       */
      if (navigator.onLine === false) {
        try {
          await enqueueOperation(queueStore, offlineActions.accept(order.orderNo));
        } catch {
          setError(BOARD_OFFLINE_QUEUE_UNAVAILABLE_TEXT);
          setAcceptingId(null);
          return;
        }
        setLocalStatus((current) => ({ ...current, [order.id]: 'ACCEPTED' }));
        setUnread((current) => {
          const next = { ...current };
          delete next[order.id];
          return next;
        });
        setToastOrderId((current) => (current === order.id ? null : current));
        setNotice(OFFLINE_ACCEPT_QUEUED_TEXT);
        setAcceptingId(null);
        return;
      }
      try {
        await acceptOrder(order.id);
        setLocalStatus((current) => ({ ...current, [order.id]: 'ACCEPTED' }));
        setUnread((current) => {
          const next = { ...current };
          delete next[order.id];
          return next;
        });
        setToastOrderId((current) => (current === order.id ? null : current));
        // ⚠️ **不**自动跳到「已接单」分区：任务书的冻结用例在接单成功后仍断言面板停在
        // 待接单（`board-empty` = 「暂无待接订单」），且收银员的下一单还在这个分区里。
      } catch (e) {
        // 抢单输家：**必须靠业务码**分辨（只看 409 会把「已被接单」与「状态不可接」混成一句）
        if (readErrorCode(e) === 'ORDER_ALREADY_ACCEPTED') {
          setError('接单失败：该订单已被其他收银端接单');
          setLocalStatus((current) => ({ ...current, [order.id]: 'ACCEPTED' }));
          setUnread((current) => {
            const next = { ...current };
            delete next[order.id];
            return next;
          });
          setToastOrderId((current) => (current === order.id ? null : current));
        } else {
          setError(readServerMessage(e) ?? '接单失败，请重试');
        }
      } finally {
        setAcceptingId(null);
      }
    },
    [queueStore],
  );

  const open = useCallback(
    (order: OrderQueueItem): void => {
      // 带上 orderId：详情页刷新前的常规路径不需要反查订单号（§4.3 的路由参数是 :orderNo）
      navigate(`/cashier/orders/${order.orderNo}`, { state: { orderId: order.id } });
    },
    [navigate],
  );

  const toastOrder = orders.find((order) => order.id === toastOrderId && order.status === 'PENDING') ?? null;
  const visible = sections[section];
  /**
   * 面板数据是否已陈旧（§6.4 规则 7 的「超过 5 分钟 ⇒ 黄色警示」）。
   *
   * 每次渲染重算：`stream.freshness` 一变、或这一页因为任何原因重渲染，警示就会跟上。
   * 刻意**不**起一个定时器去「到点自动变黄」：那会让面板上多一个与被测行为无关的时钟，
   * 而 `CashierBoardPage.spec.tsx` 全程用假定时器 —— 一条自己会走的表只会制造偶发。
   * 真实的黄色警示由下一帧（新单到达、轮询落地、用户操作）自然带出来。
   */
  const freshnessStale = isStale(stream.freshness ?? null, new Date());

  return (
    <div
      data-testid="cashier-board-page"
      data-geom-container
      data-geom-gap={String(BOARD_GEOM_GAP)}
      data-geom-width={String(BOARD_GEOM_WIDTH)}
      className="flex flex-col gap-3"
    >
      {error !== null && (
        <p
          data-testid="board-error"
          {...geomAttrs('board-error')}
          className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600"
        >
          {error}
        </p>
      )}
      {/*
       * ⚠️ **解锁提示条必须是出流浮层（`fixed`）**：它由**首次用户交互**（window 上的
       * `pointerdown` 解锁）触发出现，若插在列表**上方**的文档流里，会在同一次点击的
       * `pointerdown` 与 `pointerup` 之间把整列内容推下去 ⇒ 两个事件落在不同元素上
       * ⇒ 浏览器不派发 `click` ⇒ **第一次点「接单」被吃掉**（Task 24 真浏览器实测：
       * 点两次才生效）。同理它**不铺满**按钮区域（左下角浮层），否则又变成吞点击的遮罩。
       * `pointer-events-none`：这是只读反馈，任何情况下都不该截获手势。
       */}
      {notice !== null && (
        <p
          data-testid="sound-ready"
          role="status"
          aria-live="polite"
          {...geomAttrs('sound-ready')}
          className="pointer-events-none fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] left-4 z-[1000] rounded-lg border-2 border-black bg-gray-50 p-2 text-sm shadow-lg"
        >
          {notice}
        </p>
      )}
      {stream.level === 'manual' && (
        <p
          data-testid="offline-hint"
          {...geomAttrs('offline-hint')}
          className="flex items-center gap-2 border-b-2 border-black bg-black px-3 py-1 text-sm font-bold text-white"
        >
          <span className="h-2 w-2 rounded-full bg-white" />
          当前离线，请让顾客出示订单码
          <Link to="/cashier/scan" className="underline">
            去扫码
          </Link>
        </p>
      )}

      <div {...geomAttrs('control')} className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => navigate('/cashier/scan')}>
          扫码取单
        </Button>
        <Button variant="secondary" onClick={() => navigate('/cashier/scan')}>
          手输短码
        </Button>
        <Button onClick={() => navigate('/cashier/new')}>手动建单</Button>
      </div>

      <div {...geomAttrs('tabs')} className="flex gap-2">
        {BOARD_SECTIONS.map((value) => (
          <button
            key={value}
            type="button"
            data-testid={`board-tab-${value}`}
            onClick={() => setSection(value)}
            className={`flex-1 rounded-lg border-2 border-black px-3 py-2 text-sm font-bold ${
              section === value ? 'bg-black text-white' : 'bg-white text-black'
            }`}
          >
            {SECTION_LABELS[value]} {sections[value].length}
          </button>
        ))}
      </div>

      <p
        data-testid="order-card-count"
        {...geomAttrs('count')}
        className="text-sm font-bold text-gray-500"
      >
        {SECTION_LABELS[section]} {visible.length} 单
      </p>

      {/*
       * **数据时间戳 + 黄色警示**（§6.4 规则 7 末句：「数据超过 5 分钟未刷新时…
       * 并在接单面板顶部提示需要刷新」；§6.8 的 `payment-data-asof` / `grid-data-asof` 同款口径）。
       *
       * 时间来自 `stream.freshness` —— **本机接收一份服务端权威队列快照的时刻**，
       * 不是订单的 `createdAt`，也不是 `lastEventAt`（后者是最后一次**增量事件**到达，
       * 「有新单推来」不等于「整块面板与服务端对过账」）。
       * 刻意不给它一个 `*-asof` 的 testid：§6.8 的冻结表把 `asof` 这个词留给了弹窗与货架网格两处，
       * 这里多造一个同词根的 testid 就会落进静态⑮ 的「离线词汇未登记」判据里。
       */}
      <p
        data-testid="board-data-time"
        {...geomAttrs('data-time')}
        className={freshnessStale ? 'text-sm font-bold text-amber-600' : 'text-sm text-gray-500'}
      >
        {formatDataAsOf(stream.freshness ?? null)}
        {freshnessStale ? ' · 数据超过 5 分钟未刷新，请刷新页面' : ''}
      </p>

      {visible.length === 0 ? (
        <p data-testid="board-empty" className="py-12 text-center text-gray-500">
          {EMPTY_TEXT[section]}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {visible.map((order) => (
            <li key={order.id} {...geomAttrs('listitem')}>
              <OrderCard
                order={order}
                unread={unread[order.id] === true}
                highlight={highlighted[order.id] === true}
                accepting={acceptingId === order.id}
                onAccept={() => void accept(order)}
                onOpen={() => open(order)}
              />
            </li>
          ))}
        </ul>
      )}

      {toastOrder !== null && (
        <NewOrderToast
          order={toastOrder}
          onAccept={() => void accept(toastOrder)}
          onLater={() => setToastOrderId(null)}
        />
      )}
    </div>
  );
}
